import { useCallback, useEffect, useRef, useState } from 'react';

import { usePendingPosts } from '@/src/features/offline/use-outbox';
import { loadCachedPosts, saveCachedPosts } from './feed-cache';
import {
  FEED_RANKING_VERSION,
  getFeedPosts,
  mergeRankedPostsWithLivePosts,
  PUBLIC_POST_LIMIT,
  refreshMyRankedFeedsWithCooldown,
  subscribeToRankedPosts,
  subscribeToPublicPosts,
  type FeedMode,
  type PublicPost,
} from './post-service';

const LATEST_FEED_REFRESH_INTERVAL_MS = 60_000;

function sortPosts(posts: PublicPost[]) {
  return [...posts].sort((firstPost, secondPost) => {
    const firstMillis =
      firstPost.localCreatedAtMillis ??
      (firstPost.createdAt instanceof Date
        ? firstPost.createdAt.getTime()
        : (firstPost.createdAt?.toMillis() ?? 0));
    const secondMillis =
      secondPost.localCreatedAtMillis ??
      (secondPost.createdAt instanceof Date
        ? secondPost.createdAt.getTime()
        : (secondPost.createdAt?.toMillis() ?? 0));

    return secondMillis - firstMillis;
  });
}

export function usePublicPosts(
  uid: string | undefined,
  limitCount = PUBLIC_POST_LIMIT,
  mode: FeedMode = 'ranked',
  enabled = true,
) {
  const [effectiveLimit, setEffectiveLimit] = useState(limitCount);
  const [posts, setPosts] = useState<PublicPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const postsRef = useRef<PublicPost[]>([]);
  const rankedPostsRef = useRef<PublicPost[]>([]);
  const livePostsRef = useRef<PublicPost[]>([]);
  const rankedReadyRef = useRef(false);
  const liveReadyRef = useRef(false);
  const subscriptionKeyRef = useRef<string | undefined>(undefined);
  const pendingPosts = usePendingPosts(uid);
  const refreshRequestedRef = useRef(false);
  const cacheKey =
    mode === 'ranked' && uid
      ? `ranked:home:${uid}:v${FEED_RANKING_VERSION}`
      : mode === 'latest' && uid
        ? `latest:home:${uid}:v${FEED_RANKING_VERSION}`
        : 'public';

  useEffect(() => {
    setEffectiveLimit(limitCount);
  }, [limitCount]);

  useEffect(() => {
    const subscriptionKey = `${uid ?? ''}:${mode}`;

    if (subscriptionKeyRef.current !== subscriptionKey) {
      subscriptionKeyRef.current = subscriptionKey;
      postsRef.current = [];
      rankedPostsRef.current = [];
      livePostsRef.current = [];
      rankedReadyRef.current = false;
      liveReadyRef.current = false;
    }

    if (postsRef.current.length === 0) {
      setLoading(true);
    }
    setError(null);

    if (!enabled) {
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
      return undefined;
    }

    if (postsRef.current.length === 0) {
      void loadCachedPosts(cacheKey).then((cachedPosts) => {
        if (cachedPosts.length > 0) {
          postsRef.current = cachedPosts;
          setPosts(cachedPosts);
          setLoading(false);
        }
      });
    }

    const useRankedFeed = mode === 'ranked' && Boolean(uid);
    const useServerLatestFeed = mode === 'latest' && Boolean(uid);

    if (useServerLatestFeed) {
      let cancelled = false;
      let fallbackUnsubscribe: (() => void) | undefined;
      let latestLoadInFlight = false;

      const loadServerLatestPosts = () => {
        if (latestLoadInFlight) {
          return;
        }

        latestLoadInFlight = true;
        void getFeedPosts({
          feedKind: 'home',
          limitCount: effectiveLimit,
        })
          .then((result) => {
            if (cancelled) {
              return;
            }

            postsRef.current = result.posts;
            setPosts(result.posts);
            void saveCachedPosts(cacheKey, result.posts);
            setError(null);
            setLoading(false);
            setLoadingMore(false);
            setRefreshing(false);
          })
          .catch((caughtError) => {
            if (cancelled) {
              return;
            }

            if (
              typeof __DEV__ !== 'undefined' &&
              __DEV__ &&
              !fallbackUnsubscribe
            ) {
              fallbackUnsubscribe = subscribeToPublicPosts({
                limitCount: effectiveLimit,
                uid,
                onError: (fallbackError) => {
                  setError(fallbackError);
                  setLoading(false);
                  setLoadingMore(false);
                  setRefreshing(false);
                },
                onNext: (nextPosts) => {
                  postsRef.current = nextPosts;
                  setPosts(nextPosts);
                  void saveCachedPosts(cacheKey, nextPosts);
                  setError(null);
                  setLoading(false);
                  setLoadingMore(false);
                  setRefreshing(false);
                },
              });
              return;
            }

            setError(
              caughtError instanceof Error
                ? caughtError
                : new Error('Unable to load posts.'),
            );
            setLoading(false);
            setLoadingMore(false);
            setRefreshing(false);
          })
          .finally(() => {
            latestLoadInFlight = false;
          });
      };

      loadServerLatestPosts();
      const latestRefreshInterval = setInterval(
        loadServerLatestPosts,
        LATEST_FEED_REFRESH_INTERVAL_MS,
      );

      return () => {
        cancelled = true;
        clearInterval(latestRefreshInterval);
        fallbackUnsubscribe?.();
      };
    }

    if (useRankedFeed) {
      void refreshMyRankedFeedsWithCooldown({
        feedKind: 'home',
        uid: uid!,
      }).catch(() => undefined);
    }

    const publishRankedPosts = () => {
      if (!rankedReadyRef.current || !liveReadyRef.current) {
        return;
      }

      const mergedPosts = mergeRankedPostsWithLivePosts({
        limitCount: effectiveLimit,
        livePosts: livePostsRef.current,
        rankedPosts: rankedPostsRef.current,
      });

      postsRef.current = mergedPosts;
      setPosts(mergedPosts);
      void saveCachedPosts(cacheKey, mergedPosts);
      setError(null);
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
    };

    const unsubscribe = useRankedFeed
      ? (() => {
          const rankedUnsubscribe = subscribeToRankedPosts({
            feedKind: 'home',
            limitCount: effectiveLimit,
            uid: uid!,
            onError: (caughtError) => {
              setError(caughtError);
              setLoading(false);
              setLoadingMore(false);
              setRefreshing(false);
            },
            onNext: (nextPosts) => {
              rankedPostsRef.current = nextPosts;
              rankedReadyRef.current = true;
              publishRankedPosts();

              if (nextPosts.length === 0 && !refreshRequestedRef.current) {
                refreshRequestedRef.current = true;
                void refreshMyRankedFeedsWithCooldown({
                  feedKind: 'home',
                  force: true,
                  uid: uid!,
                }).catch(() => undefined);
              }
            },
          });
          livePostsRef.current = [];
          liveReadyRef.current = true;

          return () => {
            rankedUnsubscribe();
          };
        })()
      : subscribeToPublicPosts({
          limitCount: effectiveLimit,
          uid,
          onError: (caughtError) => {
            setError(caughtError);
            setLoading(false);
            setLoadingMore(false);
            setRefreshing(false);
          },
          onNext: (nextPosts) => {
            postsRef.current = nextPosts;
            setPosts(nextPosts);
            void saveCachedPosts('public', nextPosts);
            setError(null);
            setLoading(false);
            setLoadingMore(false);
            setRefreshing(false);
          },
        });

    return () => {
      unsubscribe();
    };
  }, [cacheKey, effectiveLimit, enabled, mode, refreshNonce, uid]);

  const hasMore = posts.length >= effectiveLimit;

  const loadMore = useCallback(() => {
    if (loading || loadingMore || refreshing || !hasMore) {
      return;
    }

    setLoadingMore(true);
    setEffectiveLimit((currentLimit) => currentLimit + PUBLIC_POST_LIMIT);
  }, [hasMore, loading, loadingMore, refreshing]);

  const refresh = useCallback(() => {
    setRefreshing(true);
    refreshRequestedRef.current = false;
    if (mode === 'ranked' && uid) {
      void refreshMyRankedFeedsWithCooldown({
        feedKind: 'home',
        force: true,
        uid,
      }).catch(() => undefined);
    }
    setRefreshNonce((currentNonce) => currentNonce + 1);
  }, [mode, uid]);

  return {
    error,
    hasMore,
    loadMore,
    loading,
    loadingMore,
    posts:
      mode === 'ranked'
        ? [...pendingPosts, ...posts]
        : sortPosts([...pendingPosts, ...posts]),
    refresh,
    refreshing,
  };
}
